/**
 * PWA lifecycle helpers.
 *
 *   registerSW()       — Register the service worker (production only).
 *   listenInstallPrompt(cb)  — Subscribe to Chrome/Android install prompt events.
 *                              cb(deferredEvent | null). Returns unsubscribe fn.
 *   promptInstall(evt) — Fire the browser install prompt.
 *   isStandalone()     — True when app is launched from the home screen.
 *   isIOS()            — True on iOS/iPadOS (needs the manual "Add to Home Screen" flow).
 */

let deferredPromptEvent = null;
const listeners = new Set();

function notify() {
  for (const fn of listeners) {
    try { fn(deferredPromptEvent); } catch (_) { /* ignore */ }
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPromptEvent = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPromptEvent = null;
    notify();
    try { localStorage.setItem("rammy:pwa:installed", "1"); } catch (_) {}
  });
}

export function listenInstallPrompt(cb) {
  listeners.add(cb);
  cb(deferredPromptEvent);
  return () => listeners.delete(cb);
}

export async function promptInstall() {
  if (!deferredPromptEvent) return { outcome: "unavailable" };
  deferredPromptEvent.prompt();
  const choice = await deferredPromptEvent.userChoice.catch(() => ({ outcome: "dismissed" }));
  deferredPromptEvent = null;
  notify();
  return choice;
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function registerSW() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Only register in production builds — dev server hot-reload conflicts with SW cache.
  if (process.env.NODE_ENV !== "production") return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .catch((err) => {
        // Silent failure — PWA install still works without SW in some browsers.
        console.debug("[Rammy PWA] SW registration skipped:", err?.message);
      });
  });
}
